import React, { useState, useEffect } from "react"

import { formatBookableEventDurationLabel } from "../../../../@core/bookableEventType/bookableEventType.utils"
import { Toggle } from "../../../../components/shared/Inputs"
import { getBuilderServiceItemLocationDetail } from "../../utils/builderServiceItemUtils"
import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"
import BackgroundColorSelector from "../shared/BackgroundColorSelector"

import AddServicesModal from "./shared/AddServicesModal"
import EditorFooter from "./shared/EditorFooter"
import ServiceImageObjectFitField from "./shared/ServiceImageObjectFitField"

const ServicesEditor = ({ section, onSave, onCancel, onDelete, isEditing = false }) => {
  const { handleUpdateSectionPreview, clearPreview, practice, pages } = useWebsiteBuilderV2()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [formData, setFormData] = useState({
    headline: section?.headline || "Services & Events",
    body: section?.body || "",
    settings: section?.settings || {
      layout: "list",
      showBackgroundColor: false,
      backgroundColor: "#ffffff",
      selectedItemIds: [],
      showButton: false,
      buttonText: "Book Now",
      buttonAction: "service",
      buttonTarget: "",
      imageObjectFit: "cover"
    }
  })

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState(null)

  const [errors, setErrors] = useState({})
  const formatServiceTargetValue = (serviceId) => `service:${serviceId}`
  const formatEventTargetValue = (eventTypeId) => `event:${eventTypeId}`
  const normalizeButtonTargetForSelect = (buttonTarget) => {
    if (!buttonTarget) return ""

    const target = String(buttonTarget)
    if (target.startsWith("service:") || target.startsWith("event:")) {
      return target
    }

    if (/^\d+$/.test(target)) {
      const eventById = eventTypes.find((event) => String(event.id) === target)
      if (eventById) return formatEventTargetValue(target)
      return formatServiceTargetValue(target)
    }

    const eventBySlug = eventTypes.find((event) => event.slug === target)
    if (eventBySlug?.id) return formatEventTargetValue(eventBySlug.id)

    return ""
  }

  useEffect(() => {
    if (section) {
      let defaultHeadline = section.headline || "Services & Events"
      if (
        section.layout === "services" &&
        (defaultHeadline.toLowerCase().includes("featured") || defaultHeadline === "featured_services")
      ) {
        defaultHeadline = "Services & Events"
      }
      setFormData({
        headline: defaultHeadline,
        body: section.body || "",
        settings: {
          layout: "list",
          showBackgroundColor: false,
          backgroundColor: "#ffffff",
          selectedItemIds: [],
          showButton: false,
          buttonText: "Book Now",
          buttonAction: "service",
          buttonTarget: "",
          ...section.settings
        }
      })
    }
  }, [section])

  // Get all available items (services, packages, events)
  // Services includes both services and packages (packages have package: true)
  const allServices = (practice?.services || []).filter((s) => s.public === true)
  const services = allServices.filter((s) => !s.package)
  const packages = allServices.filter((s) => s.package === true)
  // Show only event types (one row per type), not individual scheduled instances.
  const eventTypes = (practice?.bookableEventTypes || []).filter((e) => !e.deletedAt && !e.deleted_at)

  // Create unified list
  const allItems = [
    ...services.map((s) => ({ ...s, itemType: "service", label: "SERVICE" })),
    ...packages.map((p) => ({ ...p, itemType: "package", label: "PACKAGE" })),
    ...eventTypes.map((e) => ({ ...e, itemType: "event", label: "EVENT" }))
  ]

  // Get selected items in order
  const selectedItems = (formData.settings.selectedItemIds || [])
    .map((id) => {
      const [type, itemId] = id.split("-")
      return allItems.find((item) => item.itemType === type && (item.id?.toString() === itemId || item.slug === itemId))
    })
    .filter(Boolean)

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value }

      if (section?.id && ["headline", "subheadline", "body"].includes(field)) {
        handleUpdateSectionPreview(section.id, {
          ...section,
          ...updated
        })
      }

      return updated
    })

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }))
    }
  }

  const handleSettingsChange = (key, value) => {
    setFormData((prev) => {
      const newSettings = { ...prev.settings, [key]: value }

      const importantSettings = [
        "layout",
        "imageObjectFit",
        "showBackgroundColor",
        "backgroundColor",
        "selectedItemIds",
        "showButton",
        "buttonAction",
        "buttonTarget"
      ]
      if (section?.id && importantSettings.includes(key)) {
        handleUpdateSectionPreview(section.id, {
          ...section,
          ...prev,
          settings: newSettings
        })
      }

      return {
        ...prev,
        settings: newSettings
      }
    })
  }

  const handleSaveSelectedItems = (selectedIds) => {
    handleSettingsChange("selectedItemIds", selectedIds)
  }

  const handleRemoveItem = (item) => {
    const id = `${item.itemType}-${item.id || item.slug}`
    const newSelectedIds = (formData.settings.selectedItemIds || []).filter((selectedId) => selectedId !== id)
    handleSettingsChange("selectedItemIds", newSelectedIds)
  }

  // Drag and drop handlers
  const handleDragStart = (index) => {
    setDraggedIndex(index)
  }

  const handleDragOver = (e, index) => {
    e?.preventDefault?.()
    if (draggedIndex === null || draggedIndex === index) return

    const currentIds = formData.settings.selectedItemIds || []
    const newSelectedIds = [...currentIds]
    const draggedId = newSelectedIds[draggedIndex]
    newSelectedIds.splice(draggedIndex, 1)
    newSelectedIds.splice(index, 0, draggedId)

    handleSettingsChange("selectedItemIds", newSelectedIds)
    setDraggedIndex(index)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.headline.trim()) {
      newErrors.headline = "Section headline is required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e?.preventDefault?.()
    if (validateForm()) {
      onSave({
        layout: "services",
        ...formData
      })
    }
  }

  const handleCancelClick = () => {
    clearPreview()
    onCancel()
  }

  // Layout options
  const layoutOptions = [
    {
      id: "list",
      name: "List",
      description: "Vertical list of services",
      image: require("../../../../../assets/images/builder/v2/layout-images/list.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/list-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/list-selected.png")
    },
    {
      id: "highlights_2",
      name: "2 Highlights",
      description: "Two highlighted services in a grid",
      image: require("../../../../../assets/images/builder/v2/layout-images/2-highlights.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/2-highlights-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/2-highlights-selected.png")
    },
    {
      id: "highlights_3",
      name: "3 Highlights",
      description: "Three highlighted services in a grid",
      image: require("../../../../../assets/images/builder/v2/layout-images/3-highlights.png"),
      imageHover: require("../../../../../assets/images/builder/v2/layout-images/3-highlights-hover.png"),
      imageSelected: require("../../../../../assets/images/builder/v2/layout-images/3-highlights-selected.png")
    }
  ]

  const LayoutOption = ({ option, isSelected, onSelect }) => {
    const [isHovered, setIsHovered] = useState(false)

    const getImageSource = () => {
      if (isSelected) {
        return option.imageSelected
      } else if (isHovered) {
        return option.imageHover
      } else {
        return option.image
      }
    }

    return (
      <div
        className="cursor-pointer transition-all"
        onClick={() => onSelect(option.id)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title={option.name}>
        <img
          src={getImageSource()}
          alt={`${option.name} layout`}
          className="h-16 w-24 rounded-lg object-cover transition-all duration-200"
          onError={(e) => {
            console.warn(`Failed to load image: ${e.target.src}`) // eslint-disable-line no-console
            e.target.style.display = "none"
            e.target.nextSibling.style.display = "block"
          }}
        />
        <div className="hidden h-16 w-24 items-center justify-center rounded-lg bg-gray-200">
          <div className="text-xs text-gray-500">{option.name}</div>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Form Content */}
      <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-8 pt-6">
        {/* Layout Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-builder-border-light px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">Layout</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
              <p className="block leading-[20px]">Select a layout design for this section.</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 place-content-start items-start gap-3 text-clip p-0 [flex-flow:wrap]">
            {layoutOptions.map((option) => (
              <LayoutOption
                key={option.id}
                option={option}
                isSelected={formData.settings.layout === option.id}
                onSelect={(layoutId) => handleSettingsChange("layout", layoutId)}
              />
            ))}
          </div>
        </div>

        <ServiceImageObjectFitField
          value={formData.settings.imageObjectFit || "cover"}
          onChange={(value) => handleSettingsChange("imageObjectFit", value)}
        />

        {/* Title Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-builder-border-light px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">Title</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
              <p className="block leading-[20px]">
                Create a powerful headline to capture attention and communicate your core message.
              </p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative w-full shrink-0 rounded-lg bg-white">
              <input
                type="text"
                value={formData.headline}
                onChange={(e) => handleChange("headline", e.target.value)}
                placeholder="Services & Events"
                className="relative box-border flex w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary focus:border-builder-accent focus:outline-none"
                required
              />
            </div>
            {errors.headline && (
              <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-red-500">
                <p className="block leading-[19.5px]">{errors.headline}</p>
              </div>
            )}
          </div>
        </div>

        {/* Sub-headline Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-builder-border-light px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">Sub-headline</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
              <p className="block leading-[20px]">Add a brief sub-headline to expand on your main message.</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative w-full shrink-0 rounded-lg bg-white">
              <textarea
                value={formData.body}
                onChange={(e) => handleChange("body", e.target.value)}
                placeholder="Tell visitors about your services..."
                rows={3}
                className="relative box-border flex w-full resize-none flex-row content-stretch items-center justify-between text-clip rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary focus:border-builder-accent focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Button Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-builder-border-light px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">Button</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
              <p className="block leading-[20px]">Guide visitors to the next step with a clear check button.</p>
            </div>
          </div>
          <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
            <Toggle
              checked={formData.settings.showButton || false}
              onChange={(checked) => handleSettingsChange("showButton", checked)}
            />
            <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-builder-primary">
              <p className="block whitespace-pre leading-[24px]">Show Button</p>
            </div>
          </div>

          {formData.settings.showButton && (
            <div className="flex w-full flex-col gap-4">
              {/* Radio buttons for button action */}
              <div className="flex items-center gap-6">
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="radio"
                    name="buttonAction"
                    value="page"
                    checked={formData.settings.buttonAction === "page"}
                    onChange={(e) => handleSettingsChange("buttonAction", e.target.value)}
                    className="size-6 cursor-pointer accent-builder-secondary"
                  />
                  <span className="font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary">
                    Page
                  </span>
                </label>

                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="radio"
                    name="buttonAction"
                    value="service"
                    checked={formData.settings.buttonAction === "service"}
                    onChange={(e) => handleSettingsChange("buttonAction", e.target.value)}
                    className="size-6 cursor-pointer accent-builder-secondary"
                  />
                  <span className="font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[24px] text-builder-primary">
                    Service or Event
                  </span>
                </label>

                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="radio"
                    name="buttonAction"
                    value="external"
                    checked={formData.settings.buttonAction === "external"}
                    onChange={(e) => handleSettingsChange("buttonAction", e.target.value)}
                    className="size-6 cursor-pointer accent-builder-secondary"
                  />
                  <span className="font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary">
                    External Link
                  </span>
                </label>
              </div>

              {/* Button text input */}
              <div className="flex flex-col gap-1">
                <label className="font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-builder-primary">
                  Button text
                </label>
                <input
                  type="text"
                  value={formData.settings.buttonText || "Book Now"}
                  onChange={(e) => handleSettingsChange("buttonText", e.target.value)}
                  placeholder="Book Now"
                  className="rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] text-builder-primary focus:border-builder-accent focus:outline-none"
                />
              </div>

              {/* Page selector */}
              {formData.settings.buttonAction === "page" && (
                <div className="flex flex-col gap-1">
                  <label className="font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-builder-primary">
                    Page
                  </label>
                  <select
                    value={formData.settings.buttonTarget || ""}
                    onChange={(e) => handleSettingsChange("buttonTarget", e.target.value)}
                    className="rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] text-builder-primary focus:border-builder-accent focus:outline-none">
                    <option value="">Select a page</option>
                    {pages.map((page) => (
                      <option key={page.slug} value={page.slug}>
                        {page.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Service/Event selector */}
              {formData.settings.buttonAction === "service" && (
                <div className="flex flex-col gap-1">
                  <label className="font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-builder-primary">
                    Service or Event
                  </label>
                  <select
                    value={normalizeButtonTargetForSelect(formData.settings.buttonTarget)}
                    onChange={(e) => handleSettingsChange("buttonTarget", e.target.value)}
                    className="rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] text-builder-primary focus:border-builder-accent focus:outline-none">
                    <option value="">Select</option>
                    <optgroup label="Services">
                      {services.map((service) => (
                        <option key={`service-${service.id}`} value={formatServiceTargetValue(service.id)}>
                          {service.name}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Packages">
                      {packages.map((pkg) => (
                        <option key={`package-${pkg.id}`} value={formatServiceTargetValue(pkg.id)}>
                          {pkg.name}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Events">
                      {eventTypes.map((event) => (
                        <option key={`event-${event.id || event.slug}`} value={formatEventTargetValue(event.id)}>
                          {event.name || event.title}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  <p className="font-['Manrope:Regular',_sans-serif] text-[13px] text-builder-secondary">
                    Go to your Services to add additional options.
                  </p>
                </div>
              )}

              {/* External URL input */}
              {formData.settings.buttonAction === "external" && (
                <div className="flex flex-col gap-1">
                  <label className="font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-builder-primary">
                    External URL
                  </label>
                  <input
                    type="url"
                    value={formData.settings.buttonTarget || ""}
                    onChange={(e) => handleSettingsChange("buttonTarget", e.target.value)}
                    placeholder="https://example.com"
                    className="rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] text-builder-primary focus:border-builder-accent focus:outline-none"
                  />
                  <p className="font-['Manrope:Regular',_sans-serif] text-[13px] text-builder-secondary">
                    Enter a complete URL starting with http:// or https://
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Services Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-builder-border-light px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">Services</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
              <p className="block leading-[20px]">These Service & Events will be visible on your page.</p>
            </div>
          </div>

          {/* Selected items */}
          {selectedItems.length > 0 && (
            <div className="flex w-full flex-col gap-2">
              {selectedItems.map((item, index) => {
                const itemId = item.id || item.slug
                const displayName = item.name || item.title
                let details = ""
                if (item.itemType === "service") {
                  const price = item.amountCents
                    ? `$${(item.amountCents / 100).toFixed(0)}-$${
                        item.maxAmountCents
                          ? (item.maxAmountCents / 100).toFixed(0)
                          : (item.amountCents / 100).toFixed(0)
                      }`
                    : ""
                  const duration = item.duration ? `${item.duration}/90/120 min` : ""
                  const locations = getBuilderServiceItemLocationDetail(item)
                  details = [price, duration, locations].filter(Boolean).join(" • ")
                } else if (item.itemType === "package") {
                  const price = item.amountCents ? `$${(item.amountCents / 100).toFixed(0)}` : ""
                  const duration = item.duration ? `${item.duration}min` : ""
                  const sessions = item.sessions ? `${item.sessions} Sessions` : ""
                  const location = getBuilderServiceItemLocationDetail(item)
                  details = [price, duration, sessions, location].filter(Boolean).join(" • ")
                } else if (item.itemType === "event") {
                  const amountCents = item.amountCents ?? item.amount_cents
                  let price = ""
                  if (amountCents != null && amountCents > 0) {
                    price = `$${(amountCents / 100).toFixed(0)}`
                  } else if (amountCents === 0) {
                    price = "Free"
                  } else if (item.price) {
                    price = `$${item.price}`
                  }
                  const duration = formatBookableEventDurationLabel(item)
                  details = [price, duration].filter(Boolean).join(" • ")
                }

                return (
                  <div
                    key={`${item.itemType}-${itemId}`}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`flex cursor-move items-start justify-between rounded-lg border bg-white p-3 transition-all hover:shadow-sm ${
                      draggedIndex === index ? "border-builder-accent opacity-50" : "border-builder-border-light"
                    }`}>
                    <div className="flex flex-1 items-start gap-3">
                      <div className="flex size-6 items-center justify-center">
                        <svg
                          className="size-4 text-builder-secondary"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="mb-1 font-['Manrope:Regular',_sans-serif] text-[12px] uppercase leading-[16px] tracking-wide text-builder-secondary">
                          {item.label}
                        </div>
                        <div className="mb-1 font-['Manrope:Bold',_sans-serif] text-[14px] font-semibold leading-[20px] text-builder-primary">
                          {displayName}
                        </div>
                        {details && (
                          <div className="font-['Manrope:Regular',_sans-serif] text-[13px] leading-[19.5px] text-builder-secondary">
                            {details}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveItem(item)}
                      className="flex size-6 items-center justify-center text-[#bd654f] hover:text-[#a55a45]">
                      <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                )
              })}
            </div>
          )}

          {/* Add services link */}
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-builder-accent hover:text-builder-accent-hover">
            + Add services & events
          </button>
        </div>

        {/* Background Color Section */}
        <BackgroundColorSelector
          formData={formData}
          onSettingsChange={handleSettingsChange}
          sectionType="services"
          theme={practice?.theme}
        />
      </div>

      {/* Footer Buttons */}
      <EditorFooter onDelete={onDelete} onCancel={handleCancelClick} onSave={handleSubmit} showDelete={isEditing} />

      {/* Add Services Modal */}
      <AddServicesModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveSelectedItems}
        practice={practice}
        selectedItemIds={formData.settings.selectedItemIds || []}
      />
    </>
  )
}

export default ServicesEditor
